import {
  generateRandomCodeVerifier,
  calculatePKCECodeChallenge,
  validateAuthResponse,
  discoveryRequest,
  processDiscoveryResponse,
  authorizationCodeGrantRequest,
  processAuthorizationCodeOAuth2Response,
  isOAuth2Error,
} from "oauth4webapi";

const config = {
  issuerUri: new URL(window.env.IDENTITY_PROVIDER_URL),
  redirectUri: `${window.location.origin}/oauth`,
  client: {
    client_id: window.env.CLIENT_ID,
    token_endpoint_auth_method: "none",
  },
  authDataStorageKey: "authData",
};

async function getAuthServerMetadata() {
  const response = await discoveryRequest(config.issuerUri, {
    algorithm: "oidc",
  });
  return processDiscoveryResponse(config.issuerUri, response);
}

function epochNow() {
  return new Date().getTime() / 1000;
}

function generateNonce() {
  const arr = new Uint8Array(8);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (v) => v.toString().padStart(2, "0")).join("");
}

let tokenData = null;

async function initiateAuth(userId, verificationCode, returnPath) {
  const codeVerifier = generateRandomCodeVerifier();
  const codeChallange = await calculatePKCECodeChallenge(codeVerifier);

  const authServerMetadata = await getAuthServerMetadata();

  const nonce = generateNonce();
  const state = btoa(
    JSON.stringify({
      userId,
      nonce,
      verificationCode,
    })
  );

  sessionStorage.setItem(
    config.authDataStorageKey,
    JSON.stringify({
      codeVerifier,
      state,
      returnPath,
    })
  );

  const requestUrl = new URL(authServerMetadata.authorization_endpoint);
  requestUrl.searchParams.set("client_id", config.client.client_id);
  requestUrl.searchParams.set("scope", "forms.signing");
  requestUrl.searchParams.set("code_challenge", codeChallange);
  requestUrl.searchParams.set("code_challenge_method", "S256");
  requestUrl.searchParams.set("redirect_uri", config.redirectUri);
  requestUrl.searchParams.set("response_type", "code");
  requestUrl.searchParams.set("state", state);

  window.location.href = requestUrl;
}

export async function finishAuth(urlParams) {
  const authServerMetadata = await getAuthServerMetadata();
  const authData = JSON.parse(
    sessionStorage.getItem(config.authDataStorageKey)
  );

  const params = validateAuthResponse(
    authServerMetadata,
    config.client,
    urlParams,
    authData.state
  );

  const response = await authorizationCodeGrantRequest(
    authServerMetadata,
    config.client,
    params,
    config.redirectUri,
    authData.codeVerifier
  );

  const result = await processAuthorizationCodeOAuth2Response(
    authServerMetadata,
    config.client,
    response
  );

  if (isOAuth2Error(result)) {
    throw new Error("OAuth error");
  }

  setTimeout(() => {
    const state = JSON.parse(atob(authData.state));
    const returnPath = window.location.pathname + window.location.search;
    initiateAuth(state.userId, state.verificationCode, returnPath);
  }, result.expires_in * 1000);

  tokenData = {
    accessToken: result.access_token,
    expirationDate: epochNow() + result.expires_in,
  };

  return authData.returnPath;
}

export function authInterceptor(userId, verificationCode, requestConfig) {
  const controller = new AbortController();
  const newConfig = { ...requestConfig, signal: controller.signal };

  if (!tokenData || tokenData.expirationDate < epochNow() - 60) {
    const returnPath = window.location.pathname + window.location.search;
    initiateAuth(userId, verificationCode, returnPath);
    controller.abort();
  } else {
    newConfig.headers.Authorization = `Bearer ${tokenData.accessToken}`;
  }

  return newConfig;
}
